import { SetStateAction, useContext, useEffect, useRef, useState } from "react";

import AuthContext from "context/auth/authContext";
import { UserWithCompanyAttributes } from "context/auth/types/interfaces";
import ParamsContext from "context/params/paramsContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import {
    filteredExistingItems,
    getCustomResourceLimits
} from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { useParamsFromUrlAndBrowser } from "shared/hooks/useParamsFromUrlAndBrowser";
import {
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    FILTER_NAMES
} from "shared/types/enums";
import { Company, Filter } from "shared/types/interfaces";
import { MenuAnchorEl } from "shared/types/types";

import {
    ALL_COMPANY_TYPE_OPTIONS,
    ALL_COMPANY_TYPES,
    ROOT_FILTER
} from "../constants";
import { RootCompanyTypeOption } from "../types/types";

const useCompanyFilterContent = () => {
    const {
        queryParams,
        companyFilteredValues,
        filterDropdownOptions,
        setCompanyFilteredValues,
        setQueryParams
    } = useContext(ParamsContext);

    const { isSupportUser, user } = useContext(AuthContext);

    const { getData, handleResponse, cancelSource, isCanceled } = useApi();
    const { toSm, toMd } = useMediaQueries();

    const { intermediateRootCompanyId, companyIdFromUrlAndBrowser } =
        useParamsFromUrlAndBrowser();

    const getRootFilter = (): RootCompanyTypeOption => {
        try {
            const stored = localStorage.getItem(ROOT_FILTER);

            if (
                stored &&
                ALL_COMPANY_TYPE_OPTIONS.includes(
                    stored as RootCompanyTypeOption
                )
            ) {
                return stored as RootCompanyTypeOption;
            } else {
                return ALL_COMPANY_TYPES;
            }
        } catch {
            return ALL_COMPANY_TYPES;
        }
    };

    const [rootCompanyType, setRootCompanyType] = useState(getRootFilter);
    const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const firstRender = useRef(true);

    const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];

    const getRootCompanyTypeFilter = () =>
        rootCompanyType === ALL_COMPANY_TYPES
            ? ""
            : `&${FILTER_NAMES.CompanyType}=${rootCompanyType}`;

    const fetchRootCompanies = () =>
        getData(
            `${ENDPOINTS.Companies}${ENDPOINT_PATH_SEGMENTS.FilterList}?field=${FILTER_NAMES.CompanyId}${getRootCompanyTypeFilter()}`
        );

    useEffect(() => {
        checkAndSetRootCompany();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!rootCompanyId) return;

        let isSubscribed = true;

        const getRootCompanyList = async () => {
            try {
                setIsLoading(true);

                const { data } = await fetchRootCompanies();

                getFilteredValues(isSubscribed, rootCompanyId as string, data);
            } catch (error) {
                if (isSubscribed && !isCanceled(error)) {
                    handleResponse(error);
                    setIsLoading(false);
                }
            }
        };

        const rootCompanyOptions =
            filterDropdownOptions[FILTER_NAMES.RootCompany];

        if (isSupportUser) {
            rootCompanyOptions === undefined
                ? getRootCompanyList()
                : getFilteredValues(
                      isSubscribed,
                      rootCompanyId as string,
                      rootCompanyOptions
                  );
        } else {
            getFilteredValues(isSubscribed);
        }

        return () => {
            isSubscribed = false;
            cancelSource.cancel(API_IS_BEING_CANCELLED);
            setIsLoading(false);
        };

        // eslint-disable-next-line
    }, [rootCompanyId]);

    // Refresh root company dropdown options (filtered by type) without
    // re-fetching the selected company list
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        if (!isSupportUser || !rootCompanyId) return;

        let isSubscribed = true;

        const updateRootCompanyOptions = async () => {
            try {
                setIsLoading(true);

                const { data } = await fetchRootCompanies();

                if (!isSubscribed) return;

                setCompanyFilteredValues(companyFilteredValues, {
                    ...filterDropdownOptions,
                    [FILTER_NAMES.RootCompany]: data
                });
            } catch (error) {
                if (isSubscribed && !isCanceled(error)) {
                    handleResponse(error);
                }
            }

            if (isSubscribed) setIsLoading(false);
        };

        updateRootCompanyOptions();

        return () => {
            isSubscribed = false;
            cancelSource.cancel(API_IS_BEING_CANCELLED);
            setIsLoading(false);
        };

        // eslint-disable-next-line
    }, [rootCompanyType]);

    // Close Menu and Dialog when the screen size changes
    useEffect(() => {
        closeMenu();
        closeDialog();
    }, [toMd]);

    const openMenu = (e: { currentTarget: SetStateAction<MenuAnchorEl> }) =>
        setAnchorEl(e.currentTarget);

    const closeMenu = () => setAnchorEl(null);

    const openDialog = () => setIsDialogOpen(true);
    const closeDialog = () => setIsDialogOpen(false);

    const getCompanyIdAndName = (company: Company): Filter => ({
        id: company.id,
        name: company.name
    });

    const checkAndSetRootCompany = () => {
        const {
            company,
            company_id,
            company_level,
            company_type,
            root_client_company_id,
            customResourceLimitSettings,
            user_invites_enabled
        } = user as UserWithCompanyAttributes;

        const getRootCompany = (company: Company): Filter => ({
            id: company.id,
            name: company.name
        });

        const getUpdatedCompanyData = async () => {
            try {
                const endpoints = [
                    getData(
                        `${ENDPOINTS.Companies}/${intermediateRootCompanyId}`
                    ),
                    getData(
                        `${ENDPOINTS.Companies}/${intermediateRootCompanyId}${ENDPOINT_PATH_SEGMENTS.Attributes}`
                    )
                ];

                const [{ data }, rootCompanyAttributes] =
                    await Promise.all(endpoints);

                const customResourceLimits = await getCustomResourceLimits(
                    intermediateRootCompanyId as string,
                    getData
                );

                setQueryParams({
                    params: {
                        [FILTER_NAMES.RootCompany]: intermediateRootCompanyId
                    },
                    rootInfo: {
                        name: getRootCompany(data),
                        level: rootCompanyAttributes.data.level,
                        type: rootCompanyAttributes.data.type,
                        rootClientCompanyId:
                            rootCompanyAttributes.data.root_client_company_id,
                        customResourceLimitSettings: customResourceLimits,
                        canInviteUsers:
                            rootCompanyAttributes.data.user_invites_enabled
                    }
                });
            } catch {
                setQueryParams({
                    params: {
                        [FILTER_NAMES.RootCompany]: String(company_id),
                        [FILTER_NAMES.CompanyId]: []
                    },
                    resetPage: true,
                    rootInfo: {
                        name: getRootCompany(company),
                        level: company_level,
                        type: company_type,
                        rootClientCompanyId: root_client_company_id,
                        customResourceLimitSettings,
                        canInviteUsers: user_invites_enabled
                    }
                });
            }
        };

        const isEmpty =
            intermediateRootCompanyId === null ||
            intermediateRootCompanyId === undefined ||
            intermediateRootCompanyId === "";

        const isNumber = Number.isInteger(Number(intermediateRootCompanyId));

        const isSameAsUserCompany =
            String(company_id) === intermediateRootCompanyId;

        if (!isSupportUser || isSameAsUserCompany) {
            setQueryParams({
                params: {
                    [FILTER_NAMES.RootCompany]: String(company_id)
                },

                rootInfo: {
                    name: getCompanyIdAndName(company),
                    level: company_level,
                    type: company_type,
                    rootClientCompanyId: root_client_company_id,
                    customResourceLimitSettings,
                    canInviteUsers: user_invites_enabled
                }
            });
        } else if (isEmpty || !isNumber) {
            setQueryParams({
                params: {
                    [FILTER_NAMES.RootCompany]: String(company_id),
                    [FILTER_NAMES.CompanyId]: []
                },
                resetPage: true,
                rootInfo: {
                    name: getCompanyIdAndName(company),
                    level: company_level,
                    type: company_type,
                    rootClientCompanyId: root_client_company_id,
                    customResourceLimitSettings,
                    canInviteUsers: user_invites_enabled
                }
            });
        } else {
            getUpdatedCompanyData();
        }
    };

    const getFilteredValues = async (
        isSubscribed: boolean,
        rootId?: string,
        rootOptions?: any
    ) => {
        try {
            setIsLoading(true);

            const rootIdParams = rootId
                ? `&${FILTER_NAMES.RootCompany}=${rootId}`
                : "";

            const { data: companies } = await getData(
                `${ENDPOINTS.Companies}${ENDPOINT_PATH_SEGMENTS.FilterList}?field=${FILTER_NAMES.CompanyId}${rootIdParams}`
            );

            const values = filteredExistingItems(
                companies,
                companyIdFromUrlAndBrowser
            );

            const dropdownOptions = {
                ...(rootOptions
                    ? { [FILTER_NAMES.RootCompany]: rootOptions }
                    : {}),
                [FILTER_NAMES.CompanyId]: companies
            };

            setCompanyFilteredValues(values, dropdownOptions);
        } catch (error) {
            if (isSubscribed && !isCanceled(error)) {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    return {
        rootCompanyType,
        isLoading,
        isDialogOpen,
        anchorEl,
        rootCompanyId,
        companyFilteredValues,
        toSm,
        toMd,
        setRootCompanyType,
        openDialog,
        closeDialog,
        openMenu,
        closeMenu
    };
};

export default useCompanyFilterContent;
